import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { getAllChatMessages } from "../../services/chatService";
import "./PrivateChat.css";
import CopyButton from "../textCopyButton/CopyButton";
import { parseMessage } from "../../utils/parseMessage";

const PrivateChat = ({ user, selectedFriend, token, socket }) => {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);
  const chatContainerRef = useRef(null);
  const hasAutoScrolled = useRef(false);

  // ================= FETCH OLD MESSAGES =================
  useEffect(() => {
    if (!selectedFriend?.chatId || !token) return;
    let isMounted = true;
    setMessages([]);

    const fetchMessages = async () => {
      try {
        const res = await getAllChatMessages(selectedFriend.chatId);
        if (isMounted) setMessages(res.data || []);
      } catch (err) {
        console.error("Fetch failed:", err);
      }
    };

    fetchMessages();
    return () => {
      isMounted = false;
    };
  }, [selectedFriend?.chatId, token]);

  // ================= SOCKET JOIN + RECEIVE =================
  useEffect(() => {
    if (!socket || !selectedFriend?.chatId) return;

    socket.emit("joinRoom", selectedFriend.chatId, (res) => {
      if (!res?.joined) console.error("Failed to join room");
    });

    const handleReceiveMessage = (msg) => {
      if (msg.chatId === selectedFriend.chatId) {
        setMessages((prev) => {
          if (prev.some((m) => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
      }
    };

    const handleTyping = ({ chatId, userId, username, isTyping }) => {
      if (chatId !== selectedFriend.chatId || userId === user._id) return;
      setTypingUser(isTyping ? username : null);
    };

    const handleMessagesSeen = ({ chatId, userId }) => {
      if (chatId !== selectedFriend.chatId) return;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.sender?._id === user._id ? { ...msg, seen: true } : msg,
        ),
      );
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("typing", handleTyping);
    socket.on("messagesSeen", handleMessagesSeen);

    socket.emit("markSeen", { chatId: selectedFriend.chatId });

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("typing", handleTyping);
      socket.off("messagesSeen", handleMessagesSeen);
    };
  }, [socket, selectedFriend, user]);

  // ================= AUTO SCROLL =================
  const isUserNearBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return true;

    const threshold = 120; // px
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  useEffect(() => {
    if (!messages.length) return;

    // Force scroll on first load
    if (!hasAutoScrolled.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      hasAutoScrolled.current = true;
      return;
    }

    // Conditional scroll for new messages
    if (isUserNearBottom()) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [selectedFriend?.chatId]);

  // ================= HANDLE TYPING & TEXT =================
  const onTextChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    if (!socket || !selectedFriend?.chatId) return;

    socket.emit("typing", {
      chatId: selectedFriend.chatId,
      isTyping: value.length > 0,
    });

    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit("typing", { chatId: selectedFriend.chatId, isTyping: false });
    }, 1200);
  };

  // ================= SEND MESSAGE =================
  const sendMessage = () => {
    if (!message.trim() || !socket) return;

    const payload = {
      chatId: selectedFriend.chatId,
      senderId: user._id,
      content: message, // Now preserves \n from textarea
      clientMsgId: uuidv4(),
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    socket.emit("sendMessage", payload, (res) => {
      if (res?.status === "rateLimitExceeded") {
        alert(`You're sending messages too fast. Wait ${res.retryAfter}s`);
      } else if (res?.status !== "ok") {
        console.error("Message failed:", res);
      }
    });

    setMessage("");
    socket.emit("typing", { chatId: selectedFriend.chatId, isTyping: false });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-box">
      <div className="chat-header">
        <h3>{selectedFriend?.username}</h3>
      </div>

      <div className="chat-messages" ref={chatContainerRef}>
        {messages.map((msg) => {
          const isSelf =
            msg.sender?._id === user._id || msg.sender === user._id;
          return (
            <div
              key={msg._id || msg.clientMsgId}
              className={`message ${isSelf ? "self" : "other"}`}
            >
              <div className="message-content">
                {/* HEADER ROW: Name (if other) + Copy Button */}
                <div className="message-header-row">
                  {!isSelf && (
                    <div className="message-user-name">
                      {msg.sender?.username}
                    </div>
                  )}
                  {/* 🟢 TOP COPY BUTTON ONLY */}
                  <CopyButton text={msg.content} />
                </div>

                {/* THE FIX: This div handles the multi-line content */}
                <div className="message-text-body">
                  {parseMessage(msg.content).map((part) =>
                    part.type === "link" ? (
                      <a
                        key={part.key}
                        href={part.content}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1a73e8",
                          textDecoration: "underline",
                        }}
                      >
                        {part.content}
                      </a>
                    ) : (
                      <span key={part.key}>{part.content}</span>
                    ),
                  )}
                </div>

                <div className="message-time">
                  {msg.time}{" "}
                  {isSelf && msg.seen && <span className="seen-tick">✓✓</span>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {typingUser && (
        <div className="typing-indicator">{typingUser} is typing...</div>
      )}

      <div className="chat-input-container">
        <textarea
          value={message}
          onChange={onTextChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          className="chat-input"
          rows="1"
        />
        <button
          className="chat-send-btn"
          onClick={sendMessage}
          disabled={!message.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default PrivateChat;
